import { useMutation } from '@tanstack/react-query';
import { api } from '../lib/api-client';

// ── DTOs ────────────────────────────────────────────────────────────

interface RubroDetail {
  descripcion: string;
  unidad: string;
  cantidad_total: number;
  tarifa: number;
  subtotal: number;
}

export interface MaestroReporteDTO {
  maestro_id: string;
  maestro_nombre: string;
  maestro_cedula: string;
  rubros: RubroDetail[];
  total_rubros: number;
  total_extras: number;
  total_descuentos: number;
  neto: number;
}

export interface ReportePlanillasDTO {
  desde: string;
  hasta: string;
  project_id: string;
  project_nombre: string;
  project_codigo: string;
  maestros: MaestroReporteDTO[];
  gran_total: number;
  planillas_count: number;
}

// ── Hooks ───────────────────────────────────────────────────────────

export function useGenerateReportePlanillas(projectId: string) {
  return useMutation({
    mutationFn: async ({ desde, hasta, format, desglosar }: { desde: string; hasta: string; format: 'pdf' | 'xlsx'; desglosar?: boolean }) => {
      const response = await api.post(
        `/projects/${projectId}/reportes/planillas`,
        { desde, hasta, format, desglosar: desglosar ?? false },
        { responseType: 'blob' },
      );

      const blob = response.data as Blob;

      if (format === 'xlsx') {
        const xlsxBlob = new Blob([blob], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = URL.createObjectURL(xlsxBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `reporte-planillas-${desde}-${hasta}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
        return { format: 'xlsx' as const };
      }

      // Detect PDF by magic bytes — Content-Type header may not propagate through API Gateway/CloudFront
      const magic = await blob.slice(0, 5).text();
      if (magic.startsWith('%PDF')) {
        const pdfBlob = new Blob([blob], { type: 'application/pdf' });
        const url = URL.createObjectURL(pdfBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `reporte-planillas-${desde}-${hasta}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        // Local dev fallback: HTML preview
        const htmlBlob = new Blob([blob], { type: 'text/html' });
        const url = URL.createObjectURL(htmlBlob);
        window.open(url, '_blank');
      }
      return { format: 'pdf' as const };
    },
  });
}

export function useGenerateArchivoBancario(projectId: string) {
  return useMutation({
    mutationFn: async ({ desde, hasta }: { desde: string; hasta: string }) => {
      const response = await api.post(
        `/projects/${projectId}/reportes/banco`,
        { desde, hasta },
        { responseType: 'blob' },
      );

      const blob = new Blob([response.data as BlobPart], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `archivo-bancario-${desde}-${hasta}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
  });
}

export function useBatchPagar(projectId: string) {
  return useMutation({
    mutationFn: async ({ desde, hasta }: { desde: string; hasta: string }) => {
      const response = await api.post(
        `/projects/${projectId}/reportes/batch-pagar`,
        { desde, hasta },
      );
      return response.data as { data: { updated_count: number } };
    },
  });
}
